import React, { useState, useEffect, useRef, useMemo, useCallback, useImperativeHandle } from 'react';
import { 
  FaEdit, 
  FaInfoCircle, 
  FaCog, 
  FaPlusCircle, 
  FaTimes, 
  FaSave, 
  FaSpinner, 
  FaTimesCircle,
  FaCheckCircle,
  FaCogs,
  FaClipboardCheck, // AGGIUNTO: Nuova icona per Test Modifiche
  FaBook, // AGGIUNTO: Icona per Documentazione
  FaFilePdf // AGGIUNTO: Icona per Report PDF
} from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import FormInput from '../data/TableManager/forms/FormInput';
import CrudOperationsGrid from './CrudOperationsGrid';
import { findDifferences } from '../data/TableManager/utils/tableUtils';
import CampiTabellaModal from './CampiTabellaModal';
import CampiTableModalHub from '../Hub/CampiTableModalHub'; // Modale dedicata per modulo HUB/MILO
import FunzioniAggiuntiveModal from './FunzioniAggiuntiveModal';
import { apiService } from '../../services/apiService';
import { logger } from '../../utils/logger';
import api from '../../config/axios';
import TestModificheModal from './TestModificheModal';
import DocumentazioneWebModal from './DocumentazioneWebModal'; // AGGIUNTO: Modal documentazione web
import TestValidationStatus from './TestValidationStatus';
import RichTextEditor from '../../common/RichTextEditor'; // AGGIUNTO: Editor WYSIWYG per MAC_Modifiche
import CreateTableConfirmModal from './CreateTableConfirmModal';
import TableAcronymModal from './TableAcronymModal';
import { isHubModule } from '../../utils/moduli';

// Define which themeColors are considered "light" and need dark text
const LIGHT_THEME_COLORS = ['yellow', 'amber', 'lime', 'cyan', 'pink', 'gray', 'slate', 'white', 'emerald'];

// Componente per le opzioni tabella - SEMPLIFICATO (solo modalità espansa ma più compatta)
const SystemOptions = ({ fieldGroups, formData, handleInputChange, isProcessing, themeColor }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center space-x-2 p-3 border-b border-gray-200 dark:border-gray-700">
        <FaCog className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Opzioni Tabella</h4>
      </div>
      
      {/* Layout a griglia compatto */}
      <div className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fieldGroups.flagOptionFields.map(col => (
            <div key={col.field} className="flex items-center space-x-3 p-2 rounded-md bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  id={col.field}
                  checked={!!formData[col.field]}
                  onChange={e => handleInputChange(col.field, e.target.checked)}
                  disabled={isProcessing || col.readOnly}
                  className="sr-only"
                />
                <label 
                  htmlFor={col.field}
                  className={`
                    flex items-center justify-center w-5 h-5 rounded border-2 cursor-pointer transition-all duration-200
                    ${formData[col.field] 
                      ? `bg-${themeColor}-600 border-${themeColor}-600 dark:bg-${themeColor}-500 dark:border-${themeColor}-500` 
                      : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'
                    }
                    ${isProcessing || col.readOnly ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500 hover:scale-105'}
                    focus-within:ring-2 focus-within:ring-${themeColor}-500 focus-within:ring-offset-1
                  `}
                >
                  {formData[col.field] && (
                    <FaCheckCircle className="w-3 h-3 text-white" />
                  )}
                </label>
              </div>
              <div className="flex-1 min-w-0">
                <label 
                  htmlFor={col.field} 
                  className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none block"
                >
                  {col.label || col.field}
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MacroFunzioniDetailView = React.forwardRef(({
  selectedRow,
  columnProps = [],
  onUpdate,
  onCancel,
  inputTypes = {},
  lookupOptions = {},
  isLoadingLookups = false,
  theme = {},
  primaryKey,
  isProcessing = false,
  isCreating = false,
  onCreate,
  initialDataForCreate = {},
  onViewRelatedItems,
  relatedItemsButtonText,
  // AGGIUNTO: Ricevi le nuove props dal wrapper
  dataRefreshKey,
  onDataRefreshRequest,
}, ref) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const originalDataRef = useRef({});
  const themeColor = theme?.themeColor || 'emerald';
  
  // TEMA NATALIZIO: Flag per controlli condizionali (stili via CSS)
  const { theme: appTheme, THEMES } = useTheme();
  const isChristmas = appTheme === THEMES.CHRISTMAS;
  
  const [isCampiTabellaModalOpen, setIsCampiTabellaModalOpen] = useState(false);
  const [isFunzioniAggiuntiveModalOpen, setIsFunzioniAggiuntiveModalOpen] = useState(false);
  // AGGIUNTO: Stato per la modale Test Modifiche
  const [isTestModificheModalOpen, setIsTestModificheModalOpen] = useState(false);
  
  // AGGIUNTO: Stato per la modale Documentazione Web
  const [isDocumentazioneModalOpen, setIsDocumentazioneModalOpen] = useState(false);
  const [justCreatedId, setJustCreatedId] = useState(null);
  // NUOVO: Stato per la modale di conferma creazione tabella
  const [isCreateTableConfirmOpen, setIsCreateTableConfirmOpen] = useState(false);
  const [pendingTableCreation, setPendingTableCreation] = useState(null);
  // NUOVO: Stati per la modale acronimo tabella
  const [isTableAcronymModalOpen, setIsTableAcronymModalOpen] = useState(false);
  const [selectedAcronym, setSelectedAcronym] = useState('');
  
  // NUOVO: Stati per il censimento automatico delle funzioni
  const [censisciFunzioniFlag, setCensisciFunzioniFlag] = useState(false);
  const [isCensimentoFunzioni, setIsCensimentoFunzioni] = useState(false);
  const isMountedRef = useRef(true);

  // Stato per i campi della tabella e ID tabella corrente per la modale
  const [campiTabellaAssociata, setCampiTabellaAssociata] = useState([]);
  const [currentTableIdForModal, setCurrentTableIdForModal] = useState(null);
  const [isLoadingCampi, setIsLoadingCampi] = useState(false);
  const [isCreatingTable, setIsCreatingTable] = useState(false);
  const [campiModalRefreshKey, setCampiModalRefreshKey] = useState(0);

  // AGGIUNTO: Nuovo stato per i test
  const [testData, setTestData] = useState([]);
  const [isLoadingTests, setIsLoadingTests] = useState(false);
  const [allTestsPassed, setAllTestsPassed] = useState(true);
  const [editingTestId, setEditingTestId] = useState(null);
  const [isEditingTest, setIsEditingTest] = useState(false);
  
  // AGGIUNTO: Stato per generazione report PDF
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  
  // MOVED: Define derived state variables early to avoid initialization issues
  // const hasEssentialFields = !!(formData.MAC_Nome && formData.MAC_Id_Moduli && formData.MAC_Descrizione); // Rimosso duplicato
  const currentMacroFunzioneId = (selectedRow && selectedRow[primaryKey]) || justCreatedId;
  
  // ✅ NUOVO: Ottieni l'IdRif per le tabelle figlie
  const currentMacroFunzioneIdRif = useMemo(() => {
    if (isCreating) {
      return justCreatedId; // Per record appena creati, usa l'ID creato
    }
    // Per record esistenti, usa IdRif se disponibile, altrimenti l'ID corrente
    return selectedRow?.MAC_IdRif_MacroFunzioni || currentMacroFunzioneId;
  }, [isCreating, selectedRow, currentMacroFunzioneId, justCreatedId]);

  // Estrae le opzioni delle tabelle dai lookup per passarle al modal
  const tabelleOptions = useMemo(() => {
    const tabelleLookup = lookupOptions.MAC_Tabelle_lookup || [];
    return tabelleLookup.map(option => ({
      value: option.value,
      label: option.label,
      moduleId: option.original?.TAB_Id_Moduli
    }));
  }, [lookupOptions.MAC_Tabelle_lookup]);

  const currentModuleContext = useMemo(() => {
    const selectedModule = lookupOptions.MAC_Id_Moduli_lookup?.find(
      option => String(option.value) === String(formData.MAC_Id_Moduli)
    );
    return {
      moduleId: formData.MAC_Id_Moduli,
      moduleCode: selectedModule?.original?.MOD_CodiceModulo || selectedModule?.label || null,
    };
  }, [formData.MAC_Id_Moduli, lookupOptions.MAC_Id_Moduli_lookup]);

  const isCurrentHubModule = useMemo(
    () => isHubModule(currentModuleContext),
    [currentModuleContext]
  );

  // ALL HOOKS MUST BE DECLARED BEFORE ANY CONDITIONAL RETURNS

  // FIX 1: Memoize formColumnProps
  const formColumnProps = useMemo(() => {
    if (!Array.isArray(columnProps)) return [];
    return columnProps.filter(col => {
      if (!col) return false;
      return col.visibility > 0 && !col.isPrimaryKey;
    });
  }, [columnProps]);

  // AGGIUNTO: Funzione per caricare automaticamente i test (moved up before callbacks that use it)
  const loadTestData = useCallback(async (macroFunzioneId) => {
    // IMPORTANTE: NON caricare test se stiamo editando un test specifico
    if (isEditingTest || editingTestId) {

      return;
    }

    if (!macroFunzioneId) {
      setTestData([]);
      setAllTestsPassed(true);
      return;
    }
    

    setIsLoadingTests(true);
    try {

      
      const apiData = await apiService.get('/crud/SKE_TestModifiche', {
        params: {
          filters: JSON.stringify({ TEM_Id_MacroFunzioni: macroFunzioneId }),
          limit: 1000,
          _refresh: Date.now(), // Force cache refresh
          _bustCache: Math.random() // Additional cache busting
        }
      });

      if (apiData && apiData.records) {
        const tests = apiData.records;
        
        // Force state update even if data appears the same
        setTestData(prevTests => {
          return [...tests]; // Force new array reference
        });
        
        // Verifica se tutti i test sono superati
        const allPassed = tests.length === 0 || tests.every(test => test.TEM_TestSuperato === true || test.TEM_TestSuperato === 1);
        
        setAllTestsPassed(prevPassed => {
          return allPassed;
        });

      } else {

        setTestData([]);
        setAllTestsPassed(true);
      }
    } catch (error) {
      logger.error('[loadTestData] Errore nel caricamento dei test:', error);
      setTestData([]);
      setAllTestsPassed(true);
    } finally {
      setIsLoadingTests(false);
    }
  }, [apiService, logger, isEditingTest, editingTestId]); // AGGIUNTO: isEditingTest, editingTestId nelle dipendenze
const handleEditTest = useCallback((testId) => {

  
  // IMPORTANTE: Imposta IMMEDIATAMENTE i flag per bloccare qualsiasi refresh
  setIsEditingTest(true);
  setEditingTestId(testId);
  

  
  // Poi apri la modale con un piccolo delay per evitare race conditions
  setTimeout(() => {

    setIsTestModificheModalOpen(true);
  }, 10);
}, []);
  // FIX: Add the missing isRestrictedState function
  const isRestrictedState = useCallback((val, stateOptions = []) => {
    if (val === null || val === undefined || val === '') {

      return false;
    }
    
    const stringVal = String(val).trim();
    
    // Find the state option that matches our value
    const matchingStateOption = stateOptions.find(opt => {
      const optValue = String(opt.value).trim();
      return optValue === stringVal;
    });
    
    if (matchingStateOption) {
      const stateLabel = matchingStateOption.label || '';
      
      // Check if this is a restricted state by label
      const isRestrictedByLabel = stateLabel.toLowerCase().includes('rilasciare') || 
                                 stateLabel.toLowerCase().includes('terminata') ||
                                 stateLabel.toLowerCase().includes('da rilasciare');
      
      // Also check legacy numeric values (5 and 6) for backward compatibility
      const numVal = parseInt(stringVal, 10);
      const isRestrictedByValue = stringVal === '5' || stringVal === '6' || numVal === 5 || numVal === 6;
      
      // Check common state codes
      const isRestrictedByCode = stringVal.toLowerCase() === 't' || stringVal.toLowerCase() === 'd';
      
      const isRestricted = isRestrictedByLabel || isRestrictedByValue || isRestrictedByCode;
      return isRestricted;
    } else {
      // Fallback: check legacy numeric values if no lookup match found
      const numVal = parseInt(stringVal, 10);
      const isRestricted = stringVal === '5' || stringVal === '6' || numVal === 5 || numVal === 6;
      

      
      return isRestricted;
    }
  }, []);

  // FIX 2: Update handleInputChange with enhanced validation
  const handleInputChange = useCallback((field, value) => {
    // AGGIUNTO: Validazione speciale per MAC_Stato quando i test non sono superati
    if (field === 'MAC_Stato' && !isCreating && !allTestsPassed) {
      const statiOptions = lookupOptions.MAC_Stato_lookup || [];
      
      if (isRestrictedState(value, statiOptions)) {
        const matchingState = statiOptions.find(opt => 
          String(opt.value) === String(value) || 
          (opt.original && String(opt.original.STM_Id_StatiAttivita) === String(value))
        );
        
        const stateName = matchingState?.label || 'questo stato';
        const testFailedCount = testData.filter(t => t.TEM_Esito === 'KO').length;
        
        setErrors(prev => ({
          ...prev,
          MAC_Stato: `❌ Non puoi impostare lo stato "${stateName}" con ${testFailedCount} test falliti. Tutti i test devono essere superati prima di cambiare lo stato.`
        }));
        
        return;
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.MAC_Stato;
          return newErrors;
        });
      }
    }
    
    // ✅ NUOVO: Blocca modifica di MAC_Modifiche quando lo stato è "Terminata"
    if (field === 'MAC_Modifiche' && formData.MAC_Stato === 'T') {
      setErrors(prev => ({
        ...prev,
        MAC_Modifiche: '⚠️ Non puoi modificare questo campo con stato "Terminata". Cambia prima lo stato per poter editare le modifiche.'
      }));
      return; // Blocca la modifica
    }
    
    // Procedi normalmente con l'aggiornamento
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Rimuovi eventuali errori per questo campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [isCreating, allTestsPassed, lookupOptions, testData, errors, formData.MAC_Stato, isRestrictedState]);

  // FIX 3: Update validateForm with enhanced validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Verifica campi obbligatori basati sulla DDL - STESSA LOGICA DELLA TABELLA
    if (!formData.MAC_Id_Moduli) {
      newErrors.MAC_Id_Moduli = 'Il Modulo è obbligatorio';
    }
    
    if (!formData.MAC_Nome || formData.MAC_Nome.trim() === '') {
      newErrors.MAC_Nome = 'Il Nome è obbligatorio';
    }
    
    if (!formData.MAC_Descrizione || formData.MAC_Descrizione.trim() === '') {
      newErrors.MAC_Descrizione = 'La Descrizione è obbligatoria';
    }
    
    // ENHANCED: Validazione MAC_Stato più rigorosa con logging dettagliato
    if (!isCreating && formData.MAC_Stato !== undefined && formData.MAC_Stato !== null && !allTestsPassed) {
      const macStato = formData.MAC_Stato;
      const stateOptions = lookupOptions.MAC_Stato_lookup || lookupOptions.statilookup || [];
      
      if (isRestrictedState(macStato, stateOptions)) {
        // Trova il nome dello stato per un messaggio più chiaro
        const stateName = stateOptions.find(opt => {
          const optValue = String(opt.value).trim();
          const macString = String(macStato).trim();
          return optValue === macString;
        })?.label || `Stato ${macStato}`;
        
        newErrors.MAC_Stato = `Non è possibile impostare lo stato "${stateName}" fino a quando tutti i test non sono superati`;
        
        // Log dettagliato per debug
        console.error('🚫 [validateForm] BLOCKED save attempt with restricted state:', {
          attemptedState: macStato,
          stateType: typeof macStato,
          stringState: String(macStato),
          stateName,
          allTestsPassed,
          testCount: testData.length,
          failedTests: testData.filter(t => !t.TEM_TestSuperato).length,
          stateOptionsCount: stateOptions.length,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    // Altri campi obbligatori solo se non stiamo creando
    if (!isCreating) {
      if (!formData.MAC_Path || formData.MAC_Path.toString().trim() === '') {
        newErrors.MAC_Path = 'Il Path è obbligatorio';
      }
      
      if (!formData.MAC_Area_Params || formData.MAC_Area_Params.trim() === '') {
        newErrors.MAC_Area_Params = 'L\'Area Params è obbligatoria';
      }
      
      if (!formData.MAC_Organizzazione && formData.MAC_Organizzazione !== "0" && formData.MAC_Organizzazione !== 0) {
        newErrors.MAC_Organizzazione = 'L\'Organizzazione è obbligatoria';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isCreating, allTestsPassed, lookupOptions, testData, isRestrictedState]);

  // NUOVO: Funzione per validare i dati prima del censimento (copiata da CustomActionButtons)
  const validateCensimentoData = useCallback((macroFunzioneRecord) => {
    const details = {
      hasSelectedRow: !!macroFunzioneRecord,
      selectedRowId: macroFunzioneRecord?.MAC_Id_MacroFunzioni,
      validations: {
        macroFunzioneId: {
          value: macroFunzioneRecord?.MAC_Id_MacroFunzioni,
          valid: !!(macroFunzioneRecord?.MAC_Id_MacroFunzioni)
        },
        moduloId: {
          value: macroFunzioneRecord?.MAC_Id_Moduli,
          valid: !!(macroFunzioneRecord?.MAC_Id_Moduli)
        },
        nome: {
          value: macroFunzioneRecord?.MAC_Nome,
          valid: !!(macroFunzioneRecord?.MAC_Nome)
        }
      },
      isMounted: isMountedRef.current,
      isCensimentoInProgress: isCensimentoFunzioni
    };
    
    logger.info('[MacroFunzioniDetailView] Validation details:', details);
    return details;
  }, [isCensimentoFunzioni]);

  // NUOVO: Funzione per eseguire il censimento delle funzioni (copiata da CustomActionButtons)
  const executeCensimentoFunzioni = useCallback(async (macroFunzioneRecord) => {
    logger.info('[MacroFunzioniDetailView] ========== INIZIO CENSIMENTO FUNZIONI AUTOMATICO ==========');
    logger.info('[MacroFunzioniDetailView] isMountedRef.current:', isMountedRef.current);
    
    // Validazione preliminare estesa
    const validation = validateCensimentoData(macroFunzioneRecord);

    if (!validation.hasSelectedRow || !validation.selectedRowId) {
      const errorMsg = "Nessuna macro funzione disponibile per il censimento automatico.";
      logger.warn('[MacroFunzioniDetailView] ' + errorMsg);
      return false;
    } 

    // Validazione dettagliata dei campi obbligatori
    const missingFields = [];
    if (!validation.validations.macroFunzioneId.valid) missingFields.push('MAC_Id_MacroFunzioni');
    if (!validation.validations.moduloId.valid) missingFields.push('MAC_Id_Moduli');
    if (!validation.validations.nome.valid) missingFields.push('MAC_Nome');
    
    if (missingFields.length > 0) {
      const errorMsg = `Campi obbligatori mancanti per censimento: ${missingFields.join(', ')}`;
      logger.error('[MacroFunzioniDetailView] ' + errorMsg, { 
        missingFields, 
        macroFunzioneRecord: macroFunzioneRecord ? Object.keys(macroFunzioneRecord) : null 
      });
      return false;
    }

    if (isCensimentoFunzioni) {
      logger.warn('[MacroFunzioniDetailView] Censimento già in corso, ignoro la richiesta');
      return false;
    }

    logger.info('[MacroFunzioniDetailView] Tutte le validazioni superate - inizio processo automatico');

    setIsCensimentoFunzioni(true);
    
    try {
      // Payload corretto secondo il controller generateFunctions
      const payload = { macroFunzioneRecord };
      logger.info('[MacroFunzioniDetailView] Payload per /censimento/funzioni:', {
        payloadKeys: Object.keys(payload.macroFunzioneRecord),
        macroFunzioneId: payload.macroFunzioneRecord.MAC_Id_MacroFunzioni,
        nome: payload.macroFunzioneRecord.MAC_Nome,
        modulo: payload.macroFunzioneRecord.MAC_Id_Moduli
      });
      
      logger.info('[MacroFunzioniDetailView] Invio richiesta POST automatica a /censimento/funzioni...');
      const response = await api.post('/censimento/funzioni', payload);
      
      // Check mounted state only for state updates
      if (!isMountedRef.current) {
        logger.warn('[MacroFunzioniDetailView] Componente smontato durante la richiesta, ignoro la risposta');
        return false;
      }
      
      logger.info('[MacroFunzioniDetailView] Risposta ricevuta da /censimento/funzioni:', {
        status: response.status,
        success: response.data?.success,
        message: response.data?.message,
        dataKeys: response.data?.data ? Object.keys(response.data.data) : null
      });

      if (response.data?.success) {
        const successMsg = response.data.message || "Funzioni censite automaticamente con successo.";
        logger.info('[MacroFunzioniDetailView] Censimento automatico completato con successo:', successMsg);
        return true;
      } else {
        const errorMsg = response.data.message || "Errore durante il censimento automatico delle funzioni.";
        logger.error('[MacroFunzioniDetailView] Errore risposta API censimento automatico:', errorMsg);
        return false;
      }
    } catch (err) {
      if (!isMountedRef.current) {
        logger.warn('[MacroFunzioniDetailView] Componente smontato durante gestione errore');
        return false;
      }
      
      logger.error("[MacroFunzioniDetailView] Errore API Censimento Automatico Funzioni:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        url: err.config?.url,
        method: err.config?.method
      });
      
      return false;
    } finally {
      // Always reset loading state, even if component is unmounting
      setIsCensimentoFunzioni(false);
      logger.info('[MacroFunzioniDetailView] ========== FINE CENSIMENTO FUNZIONI AUTOMATICO ==========');
    }
  }, [validateCensimentoData, isCensimentoFunzioni]);

  // Cleanup effect for mounted ref
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // NUOVO: Funzione per generare e scaricare il report PDF
  const handleDownloadReport = useCallback(async () => {
    const functionName = 'handleDownloadReport';
    const macroFunzioneId = currentMacroFunzioneId;

    if (!macroFunzioneId) {
      logger.error(`[${functionName}] Nessun ID macrofunzione disponibile`);
      return;
    }

    logger.info(`[${functionName}] Inizio download report per macrofunzione ID: ${macroFunzioneId}`);
    setIsGeneratingReport(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await api.get(`/report/macrofunzione/${macroFunzioneId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob' // Importante per gestire il PDF
      });

      // Crea un blob dal response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Crea un URL temporaneo per il blob
      const url = window.URL.createObjectURL(blob);
      
      // Crea un elemento <a> temporaneo per il download
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_macrofunzione_${macroFunzioneId}.pdf`;
      
      // Simula il click per avviare il download
      document.body.appendChild(link);
      link.click();
      
      // Pulizia
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      logger.info(`[${functionName}] Report scaricato con successo`);
      
    } catch (error) {
      logger.error(`[${functionName}] Errore durante il download del report:`, error);
      
      // Mostra un messaggio di errore all'utente
      if (error.response?.status === 404) {
        alert('Macrofunzione non trovata');
      } else {
        alert('Errore durante la generazione del report PDF. Riprova più tardi.');
      }
    } finally {
      setIsGeneratingReport(false);
    }
  }, [currentMacroFunzioneId]);

  // FIX 4: Enhanced handleSubmit with COMPREHENSIVE state validation
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // IMPORTANTE: Se stiamo editando un test, NON procedere con il submit
    if (isEditingTest || editingTestId) {
      return;
    }
    
    // VALIDAZIONE ESSENZIALE: Nome, Modulo e Descrizione devono essere sempre presenti
    if (!formData.MAC_Nome || !formData.MAC_Id_Moduli || !formData.MAC_Descrizione) {
      console.error('❌ [handleSubmit] Campi essenziali mancanti:', {
        MAC_Nome: formData.MAC_Nome,
        MAC_Id_Moduli: formData.MAC_Id_Moduli, 
        MAC_Descrizione: formData.MAC_Descrizione
      });
      setErrors(prev => ({
        ...prev,
        general: 'Nome, Modulo e Descrizione sono obbligatori.'
      }));
      return;
    }
    
    // ENHANCED: Validazione finale degli stati riservati - SUPER RIGOROSA
    if (!isCreating) {
      const macStato = formData.MAC_Stato;
      const stateOptions = lookupOptions.MAC_Stato_lookup || lookupOptions.statilookup || [];
      
      // VALIDATION 1: Se il MAC_Stato è definito e i test non sono superati
      if (macStato !== undefined && macStato !== null && !allTestsPassed) {
        if (isRestrictedState(macStato, stateOptions)) {
          const stateName = stateOptions.find(opt => {
            const optValue = String(opt.value).trim();
            const macString = String(macStato).trim();
            return optValue === macString;
          })?.label || `Stato ${macStato}`;
          
          console.error('🚫 [handleSubmit] FINAL BARRIER: Blocked save with restricted state:', {
            attemptedState: macStato,
            stateType: typeof macStato,
            stringState: String(macStato),
            stateName,
            allTestsPassed,
            testCount: testData.length,
            failedTests: testData.filter(t => !t.TEM_TestSuperato).length,
            formDataKeys: Object.keys(formData),
            timestamp: new Date().toISOString()
          });
          
          setErrors(prev => ({
            ...prev,
            general: `🚫 SALVATGIO BLOCCATO: Lo stato "${stateName}" può essere impostato solo quando tutti i test sono superati. Completare i test prima di procedere.`,
            MAC_Stato: `Lo stato "${stateName}" è riservato per macro funzioni con test completi`
          }));
          return; // BLOCK SAVE
        }
      }
      
      // VALIDATION 2: Se stiamo tentando di salvare stati riservati in qualsiasi caso con test non superati
      if (!allTestsPassed && isRestrictedState(macStato, stateOptions)) {
        console.error('🚫 [handleSubmit] SECONDARY BARRIER: Attempted to save restricted state without all tests passed');
        
        setErrors(prev => ({
          ...prev,
          general: `🚫 SALVATGIO BLOCCATO: Non è possibile salvare con stato ${macStato} finché tutti i test non sono superati.`
        }));
        return; // BLOCK SAVE
      }
      
      // VALIDATION 3: Check if we're trying to update to a restricted state (for updates)
      if (selectedRow && selectedRow[primaryKey]) {
        const originalState = originalDataRef.current?.MAC_Stato;
        
        if (originalState !== macStato && isRestrictedState(macStato, stateOptions) && !allTestsPassed) {
          console.error('🚫 [handleSubmit] TERTIARY BARRIER: Attempted to change to restricted state in update');
          
          setErrors(prev => ({
            ...prev,
            general: `🚫 MODIFICA BLOCCATA: Non è possibile cambiare lo stato a ${macStato} senza aver superato tutti i test.`
          }));
          return; // BLOCK SAVE
        }
      }
    }
    
    // Prepara i dati per il salvataggio
    let processedFormData = { 
      ...formData,
      MAC_Nome_Tabelle: formData.MAC_Nome_Tabelle || '',
      // AGGIUNTO: Includi sempre i valori dei checkbox per garantire la sincronizzazione
      MAC_CreaItem: formData.MAC_CreaItem !== undefined ? !!formData.MAC_CreaItem : true,
      MAC_CreaItemFiltro: formData.MAC_CreaItemFiltro !== undefined ? !!formData.MAC_CreaItemFiltro : true,
      MAC_MultiAzienda: formData.MAC_MultiAzienda !== undefined ? !!formData.MAC_MultiAzienda : false,
    };
    
    const isValid = validateForm();
    
    if (!isValid) {
      console.error('❌ [handleSubmit] Validation failed, errors:', errors);
      return;
    }
    
    // MODIFIED: Call performSubmit directly without await to avoid dependency issues
    try {
      if (isCreating) {
        // ✅ Per la creazione: se lo stato è Terminata, azzera MAC_Modifiche
        if (processedFormData.MAC_Stato === 'T') {
          console.log('🧹 [handleSubmit] Azzeramento MAC_Modifiche per stato Terminata (creazione)');
          processedFormData.MAC_Modifiche = '';
        }
        
        const created = await onCreate(processedFormData);
        
        if (created && created[primaryKey]) {
          setJustCreatedId(created[primaryKey]);
          
          // CENSO AUTOMATICO dopo la creazione se richiesto
          if (censisciFunzioniFlag) {
            await executeCensimentoFunzioni(created);
          }
        }
      } else {
        // Per gli aggiornamenti, calcola le differenze
        const originalData = originalDataRef.current || {};
        const differences = findDifferences(originalData, processedFormData);
        
        // CORREZIONE: Se i checkbox non sono nelle differenze ma sono presenti nei dati, aggiungili
        if (processedFormData.MAC_CreaItem !== undefined && !('MAC_CreaItem' in differences)) {
          differences.MAC_CreaItem = processedFormData.MAC_CreaItem;
        }
        if (processedFormData.MAC_CreaItemFiltro !== undefined && !('MAC_CreaItemFiltro' in differences)) {
          differences.MAC_CreaItemFiltro = processedFormData.MAC_CreaItemFiltro;
        }
        if (processedFormData.MAC_MultiAzienda !== undefined && !('MAC_MultiAzienda' in differences)) {
          differences.MAC_MultiAzienda = processedFormData.MAC_MultiAzienda;
        }
        
        // ✅ CRITICO: Se lo stato corrente è Terminata, FORZA MAC_Modifiche = '' SEMPRE
        if (processedFormData.MAC_Stato === 'T') {
          differences.MAC_Modifiche = '';
          console.log('🧹 [handleSubmit] FORZATO MAC_Modifiche vuoto per stato Terminata (aggiornamento)', {
            statoCorrente: processedFormData.MAC_Stato,
            modifichePrecedenti: originalData.MAC_Modifiche,
            modificheNuove: differences.MAC_Modifiche
          });
        }
        
        const payload = { ...differences, [primaryKey]: selectedRow[primaryKey] };
        
        console.log('📦 [handleSubmit] Payload finale per update:', {
          payloadKeys: Object.keys(payload),
          MAC_Stato: payload.MAC_Stato,
          MAC_Modifiche: payload.MAC_Modifiche,
          hasMACModifiche: 'MAC_Modifiche' in payload
        });
        
        if (Object.keys(differences).length > 0 || censisciFunzioniFlag) {
          await onUpdate(payload);
          
          // CENSO AUTOMATICO dopo l'aggiornamento se richiesto
          if (censisciFunzioniFlag) {
            await executeCensimentoFunzioni(processedFormData);
          }
        } else {
          console.log('ℹ️ [handleSubmit] Nessuna modifica rilevata, skip update');
        }
      }
    } catch (error) {
      console.error('❌ [handleSubmit] Error during submit:', error);
      setErrors(prev => ({
        ...prev,
        general: error.message || 'Errore durante il salvataggio'
      }));
    }
    
}, [formData, isCreating, onCreate, primaryKey, validateForm, selectedRow, onUpdate, errors, censisciFunzioniFlag, executeCensimentoFunzioni, allTestsPassed, lookupOptions, testData, originalDataRef, isRestrictedState, isEditingTest, editingTestId]);

  // Esponi metodi tramite ref per il MinimizedEditForm (Quick Save Toolbar)
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {

      // Crea un evento sintetico per triggerare handleSubmit
      const syntheticEvent = {
        preventDefault: () => {}
      };
      handleSubmit(syntheticEvent);
    }
  }), [handleSubmit]);

  // FIX 5: Memoize field groupings - Area Params resta nelle generalFields
  const fieldGroups = useMemo(() => ({
    generalFields: formColumnProps.filter(col => ['MAC_Id_Moduli', 'MAC_Nome_Tabelle', 'MAC_Nome', 'MAC_Stato', 'MAC_Priorita', 'MAC_Area_Params'].includes(col.field)),
    descriptiveFields: formColumnProps.filter(col => ['MAC_Descrizione', 'MAC_Modifiche'].includes(col.field)),
    configFields: formColumnProps.filter(col => [].includes(col.field)),
    flagOptionFields: formColumnProps.filter(col => ['MAC_CreaItem', 'MAC_CreaItemFiltro', 'MAC_MultiAzienda'].includes(col.field))
  }), [formColumnProps]);

  // FIX 6: Update renderFormFieldSpecial with enhanced filtering
  const renderFormFieldSpecial = useCallback((col) => {
    let fieldLookupOptions = col.lookupName ? lookupOptions[col.lookupName] : lookupOptions[col.field];

    if (col.field === 'MAC_Id_Moduli' && col.lookupName === 'MAC_Id_Moduli_lookup' && Array.isArray(fieldLookupOptions)) {
      fieldLookupOptions = fieldLookupOptions.map(opt => {
        const originalLabel = opt.original?.MOD_CodiceModulo || opt.label || '';
        const cleanedLabel = originalLabel.replace(/[^A-Z0-9]/gi, '').substring(0, 3).toUpperCase();
        const finalLabel = cleanedLabel.length > 0 ? cleanedLabel : (opt.label || 'TAB').substring(0, 3).toUpperCase();
        
        return {
          ...opt,
          label: `[${finalLabel}] ${opt.original?.MOD_DescrizioneModulo || opt.label || ''}`
        };
      });
    }

    // ENHANCED: Validazione speciale per MAC_Stato con logging dettagliato
    if (col.field === 'MAC_Stato' && !isCreating) {
      // Filtra le opzioni per rimuovere stati riservati se non tutti i test sono superati
      if (!allTestsPassed && Array.isArray(fieldLookupOptions)) {
        const originalOptions = fieldLookupOptions;
        
        fieldLookupOptions = fieldLookupOptions.filter(option => {
          const isRestricted = isRestrictedState(option.value, originalOptions);
          
          if (isRestricted) {
            // Option is restricted and will be filtered out
          }
          
          return !isRestricted;
        });
        
        // Log per debug dettagliato
        if (originalOptions.length !== fieldLookupOptions.length) {
          // Some options were filtered out due to restricted state
        }
      }
    }

    // AGGIUNTO: Rendering custom per MAC_Modifiche con editor WYSIWYG (salva HTML)
    if (col.field === 'MAC_Modifiche') {
      // Non renderizzare finché formData non è stato popolato (in edit mode)
      const isFormDataLoaded = isCreating || (formData.MAC_Id_MacroFunzioni !== undefined);
      
      if (!isFormDataLoaded) {
        return (
          <div key={col.field} className="w-full">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              {col.label || col.field}
            </label>
            <div className="h-32 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse" />
          </div>
        );
      }
      
      return (
        <div key={col.field} className="w-full">
          <RichTextEditor
            key={`mac-modifiche-${currentMacroFunzioneId || 'new'}-${dataRefreshKey || 0}`}
            id="MAC_Modifiche"
            label={col.label || col.field}
            required={col.required}
            value={formData['MAC_Modifiche'] || ''}
            onChange={(content) => handleInputChange('MAC_Modifiche', content)}
            disabled={isProcessing || col.readOnly}
            readOnly={col.readOnly}
            placeholder={col.placeholder || 'Descrivi i dettagli delle modifiche...'}
            error={errors['MAC_Modifiche']}
            themeColor={themeColor}
            minHeight="120px"
            maxHeight="350px"
          />
        </div>
      );
    }

    return (
      <FormInput
        key={col.field}
        field={col.field}
        label={col.label || col.field}
        value={formData[col.field]}
        onChange={handleInputChange}
        inputType={inputTypes[col.field] || col.type || (Array.isArray(fieldLookupOptions) && fieldLookupOptions.length > 0 ? 'select' : 'text')}
        lookupOptions={fieldLookupOptions}
        isLoadingLookups={isLoadingLookups}
        placeholder={col.placeholder || ''}
        disabled={isProcessing || col.readOnly}
        readOnly={col.readOnly}
        error={errors[col.field]}
        themeColor={themeColor}
        required={col.required && !col.readOnly}
        showForeignKeyInfo={col.field.endsWith('Id_Tabelle')} // MODIFICATO: Solo per campi che finiscono con Id_Tabelle
      />
    );
  }, [formData, handleInputChange, inputTypes, lookupOptions, isLoadingLookups, isProcessing, themeColor, errors, isCreating, allTestsPassed, testData.length, isRestrictedState, currentMacroFunzioneId, dataRefreshKey]);

  const fetchCampiPerTabella = useCallback(async (tableId) => {
    if (!tableId) {
      setCampiTabellaAssociata([]);
      setCurrentTableIdForModal(null);
      return;
    }

    setIsLoadingCampi(true);
    
    try {
      const apiData = await apiService.get('/crud/SKE_Campi', {
        params: {
          filters: JSON.stringify({ CAM_Id_Tabelle: tableId }),
          limit: 1000,
          // AGGIUNTO: Parametro per evitare cache
          _refresh: Date.now()
        }
      });

      if (apiData && apiData.records) {
        const fetchedCampi = apiData.records.sort((a, b) => (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0));
        
        setCampiTabellaAssociata(prevCampi => {
          // MODIFICATO: Forza sempre l'aggiornamento incrementando la chiave refresh

          setCampiModalRefreshKey(prev => prev + 1);
          return fetchedCampi;
        });
        
        // MODIFICATO: Aggiorna currentTableIdForModal solo se necessario
        if (currentTableIdForModal !== tableId) {
          setCurrentTableIdForModal(tableId);
        }
      } else {
        logger.warn('[MacroFunzioniDetailView] Risposta API non contiene records validi:', apiData);
        setCampiTabellaAssociata([]);
        setCurrentTableIdForModal(null);
      }
    } catch (error) {
      logger.error('[MacroFunzioniDetailView] Errore nel fetch dei campi tabella:', error);
      setCampiTabellaAssociata([]);
      setCurrentTableIdForModal(null);
    } finally {
      setIsLoadingCampi(false);
    }
  }, [apiService, logger, currentTableIdForModal]);

  const handleOpenCampiTabellaModal = async () => {
    if (!formData.MAC_Nome || !formData.MAC_Id_Moduli || !formData.MAC_Descrizione) {
      setErrors(prev => ({
        ...prev,
        campiTabella: 'Nome, Modulo e Descrizione sono obbligatori per gestire la tabella'
      }));
      return;
    }

    if (!formData.MAC_Id_Tabelle || formData.MAC_Id_Tabelle === 0 || formData.MAC_Id_Tabelle === '0') {
      // Prepara i dati per la modale di conferma
      const selectedModule = lookupOptions.MAC_Id_Moduli_lookup?.find(m => m.value === formData.MAC_Id_Moduli);
      let moduleCode = '';
      
      if (selectedModule) {
        const originalLabel = selectedModule.original?.MOD_CodiceModulo || selectedModule.label || '';
        moduleCode = originalLabel.replace(/[^A-Z0-9]/gi, '').substring(0, 3).toUpperCase();
        if (moduleCode.length === 0) {
          moduleCode = (selectedModule.label || 'TAB').substring(0, 3).toUpperCase();
        }
      }

      // STEP 1: Prima apri la modale per l'acronimo
      setPendingTableCreation({
        moduleData: {
          ...selectedModule,
          prefix: moduleCode || 'TAB',
          label: selectedModule?.label || 'N/A'
        }
      });
      setIsTableAcronymModalOpen(true);
      return; // Esci qui, il flusso continuerà dopo la selezione dell'acronimo
    } else {
      await fetchCampiPerTabella(formData.MAC_Id_Tabelle);
    }

    setIsCampiTabellaModalOpen(true);
  };

  // Replace the createAssociateAndFetchCampi function with this version that has extensive debugging:

  const createAssociateAndFetchCampi = useCallback(async (overrideTableName) => {

    
    if (!formData.MAC_Id_Moduli || !formData.MAC_Nome || !formData.MAC_Descrizione) {
      console.error('❌ [createAssociateAndFetchCampi] Missing required fields:', {
        MAC_Id_Moduli: formData.MAC_Id_Moduli,
        MAC_Nome: formData.MAC_Nome,
        MAC_Descrizione: formData.MAC_Descrizione
      });
      throw new Error('Modulo, Nome e Descrizione sono obbligatori per creare una tabella');
    }



    try {

      setIsCreatingTable(true);
      
      logger.info('Inizio creazione tabella e associazione', {
        modulo: formData.MAC_Id_Moduli,
        nome: formData.MAC_Nome,
        descrizione: formData.MAC_Descrizione,
        isCreating
      });

      // FIX: Extract module code from lookup for TAB_Prefisso
      const selectedModule = lookupOptions.MAC_Id_Moduli_lookup?.find(m => m.value === formData.MAC_Id_Moduli);
      let moduleCode = '';
      
      if (selectedModule) {
        const originalLabel = selectedModule.original?.MOD_CodiceModulo || selectedModule.label || '';
        moduleCode = originalLabel.replace(/[^A-Z0-9]/gi, '').substring(0, 3).toUpperCase();
        if (moduleCode.length === 0) {
          moduleCode = (selectedModule.label || 'TAB').substring(0, 3).toUpperCase();
        }
      }

      // TAB_Nome è sempre il plurale (MAC_Nome). Il singolare inserito dall'utente
      // nella modale HUB va in TAB_NomeSingolare.
      const singolare = (typeof overrideTableName === 'string' && overrideTableName.trim())
        ? overrideTableName.trim()
        : null;
      const nuovaTabellaData = {
        TAB_Nome: formData.MAC_Nome,
        ...(isCurrentHubModule && singolare ? { TAB_NomeSingolare: singolare } : {}),
        TAB_Id_Moduli: formData.MAC_Id_Moduli,
        TAB_Descrizione: formData.MAC_Descrizione,
        TAB_Prefisso: moduleCode,
        TAB_AcronimoCampi: selectedAcronym || "", // Usa l'acronimo selezionato
        TAB_Id_Motori: 2,
        TAB_UpdateTimestamp: 0,
        TAB_Cache: 0,
        TAB_Cronologia: 1,
        TAB_GeneraFiltro: 0,
        TAB_DatiInserimento: 1,
        TAB_DatiModifica: 1,
      };



      // Formato corretto per l'API CRUD - usa array changes
      const requestPayload = {
        changes: [
          {
            operation: 'create',
            data: nuovaTabellaData
          }
        ]
      };




      // CORREZIONE: Endpoint corretto con /api
      const responseData = await apiService.post('/crud/SKE_Tabelle', requestPayload);
      



      // CORREZIONE: Estrazione ID dalla struttura corretta della risposta
      if (responseData && responseData.results && responseData.results.success && 
          responseData.results.results && responseData.results.results.length > 0) {
        
        const createdTableResult = responseData.results.results[0];
        const newTableId = createdTableResult.id;
        

        logger.info('Tabella creata con successo, ID:', newTableId);

        if (!newTableId) {
          throw new Error('ID tabella non trovato nella risposta del server');
        }

        // MODIFICATO: Aggiornamento più stabile del form data
        setFormData(prevFormData => {
          const updatedFormData = {
            ...prevFormData,
            MAC_Id_Tabelle: newTableId
          };

          return updatedFormData;
        });
        
        // MODIFICATO: Imposta l'ID per il modal senza causare re-render
        setCurrentTableIdForModal(newTableId);
        
        // Se siamo in modalità edit, aggiorna anche il record nel database
        if (!isCreating && selectedRow?.[primaryKey]) {

          logger.info('Aggiornamento associazione tabella per macro funzione esistente');
          
          const updatePayload = {
            changes: [{
              operation: 'update',
              data: {
                MAC_Id_MacroFunzioni: selectedRow[primaryKey],
                MAC_Id_Tabelle: newTableId
              }
            }]
          };
          
          await apiService.post(`/crud/${selectedRow._tableName || 'SKE_MacroFunzioni'}`, updatePayload);
        }
        
        // MODIFICATO: Inizializza i campi con array vuoto senza fetch
        setCampiTabellaAssociata([]);
        

        logger.info('Processo di creazione tabella e associazione completato con successo');
        
      } else {
        console.error('❌ [createAssociateAndFetchCampi] Unexpected response structure:', responseData);
        logger.error('Struttura risposta inaspettata:', responseData);
        throw new Error('Risposta del server non valida per la creazione della tabella');
      }

    } catch (error) {
      console.error('❌ [createAssociateAndFetchCampi] Error occurred:', error);
      logger.error('Errore in createAssociateAndFetchCampi:', error);
      if (error.response) {
        console.error('❌ [createAssociateAndFetchCampi] Server response error:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        logger.error('Dettagli errore risposta server:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      throw error;
    } finally {

      setIsCreatingTable(false);
    }
  }, [formData, lookupOptions.MAC_Id_Moduli_lookup, primaryKey, selectedRow, isCreating, selectedAcronym, isCurrentHubModule]);

  const previousStateRef = useRef(null);

  useEffect(() => {

    
    // CRITICAL FIX: Only update if the actual data has changed, not just references
    const currentRowId = selectedRow?.[primaryKey];
    const previousState = previousStateRef.current;
    
    // Check if we actually need to update the form data
    const shouldUpdate = (
      // First load or switching between create/edit modes
      !previousState ||
      previousState.isCreating !== isCreating ||
      // Switching to a different row
      previousState.selectedRowId !== currentRowId ||
      // DataRefreshKey changed (external refresh request)
      previousState.dataRefreshKey !== dataRefreshKey
    );
    
    if (shouldUpdate) {
        if (isCreating) {

            // CORREZIONE: Assicurati che i valori di default dei checkbox siano sempre impostati
            const defaultFormData = {
                ...initialDataForCreate,
                MAC_CreaItem: initialDataForCreate.MAC_CreaItem !== undefined ? !!initialDataForCreate.MAC_CreaItem : true,
                MAC_CreaItemFiltro: initialDataForCreate.MAC_CreaItemFiltro !== undefined ? !!initialDataForCreate.MAC_CreaItemFiltro : true,
                MAC_MultiAzienda: initialDataForCreate.MAC_MultiAzienda !== undefined ? !!initialDataForCreate.MAC_MultiAzienda : false,
            };
            setFormData(defaultFormData);
            originalDataRef.current = defaultFormData;
        } else if (selectedRow && Object.keys(selectedRow).length > 0) {
            // CORREZIONE: Assicurati che i valori di default dei checkbox siano sempre impostati anche in modifica
            const editFormData = {
                ...selectedRow,
                MAC_CreaItem: selectedRow.MAC_CreaItem !== undefined ? !!selectedRow.MAC_CreaItem : true,
                MAC_CreaItemFiltro: selectedRow.MAC_CreaItemFiltro !== undefined ? !!selectedRow.MAC_CreaItemFiltro : true,
                MAC_MultiAzienda: selectedRow.MAC_MultiAzienda !== undefined ? !!selectedRow.MAC_MultiAzienda : false,
            };
            setFormData(editFormData);
            originalDataRef.current = editFormData;
        }
        
        setErrors({});
        
        // Store current state for next comparison
        previousStateRef.current = {
            isCreating,
            selectedRowId: currentRowId,
            dataRefreshKey
        };
    } else {

    }
  }, [selectedRow, isCreating, initialDataForCreate, primaryKey, dataRefreshKey]); // AGGIUNTA LA DIPENDENZA FONDAMENTALE

  // Aggiungi questo useEffect dopo il useEffect principale che gestisce selectedRow
  useEffect(() => {

    
    // Se abbiamo una tabella associata e la DetailView è già stata inizializzata
    if (formData.MAC_Id_Tabelle && !isCreating) {

      fetchCampiPerTabella(formData.MAC_Id_Tabelle);
    }
  }, [dataRefreshKey, isCreating, fetchCampiPerTabella]);

  // MODIFICATO: Il callback ora chiama solo la prop ricevuta SENZA causare side effects
  const handleFunzioniAggiuntiveChanged = useCallback(() => {

    if (onDataRefreshRequest) {
        // IMPORTANTE: Non chiamare onDataRefreshRequest alla chiusura della modale
        // perché potrebbe causare la chiusura del MacroFunzioniDetailView

        onDataRefreshRequest();
    }
  }, [onDataRefreshRequest]);

  // NUOVO: Callback separato per la chiusura della modale
  const handleFunzioniAggiuntiveClose = useCallback(() => {

    setIsFunzioniAggiuntiveModalOpen(false);
    // NON chiamare handleFunzioniAggiuntiveChanged() qui per evitare side effects
  }, []);

  // NUOVO: Handler per la conferma creazione tabella dalla modale
  const handleCreateTableConfirm = useCallback(async (overrideTableName) => {
    setIsCreateTableConfirmOpen(false);
    setPendingTableCreation(null);
    
    try {
      setErrors(prev => ({ ...prev, campiTabella: undefined }));
      // overrideTableName arriva dalla modale (es. nome al singolare per il modulo HUB).
      await createAssociateAndFetchCampi(overrideTableName);
      // Dopo la creazione, apri automaticamente la modale dei campi
      setIsCampiTabellaModalOpen(true);
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        campiTabella: 'Errore nella creazione della tabella'
      }));
    }
  }, [createAssociateAndFetchCampi]);

  // NUOVO: Handler per l'annullamento creazione tabella dalla modale
  const handleCreateTableCancel = useCallback(() => {
    setIsCreateTableConfirmOpen(false);
    setPendingTableCreation(null);
  }, []);

  // NUOVO: Handler per la selezione dell'acronimo
  const handleAcronymSelected = useCallback((acronym) => {
    setSelectedAcronym(acronym);
    setIsTableAcronymModalOpen(false);
    // STEP 2: Dopo aver selezionato l'acronimo, apri la modale di conferma
    setIsCreateTableConfirmOpen(true);
  }, []);

  // NUOVO: Handler per l'annullamento dalla modale acronimo
  const handleAcronymCancel = useCallback(() => {
    setIsTableAcronymModalOpen(false);
    setPendingTableCreation(null);
    setSelectedAcronym('');
  }, []);

  // AGGIUNTO: Handler per la chiusura della modale Test Modifiche
  const handleTestModificheClose = useCallback(() => {

   
  // PRIMA: Chiudi la modale
  setIsTestModificheModalOpen(false);
  
  // SECONDO: Reset IMMEDIATAMENTE i flag di editing
  setIsEditingTest(false);
  setEditingTestId(null);
  

  
  // TERZO: DOPO aver resettato i flag, forza il reload dei test
  setTimeout(() => {
    if (currentMacroFunzioneId) {

      loadTestData(currentMacroFunzioneId);
    }
  }, 100);

  // RIMOSSO: Non chiamare onDataRefreshRequest per evitare chiusura dell'EditForm
  // setTimeout(() => {
  //   if (onDataRefreshRequest) {
  //     console.log('[MacroFunzioniDetailView] Calling parent refresh after everything is done');
  //     onDataRefreshRequest();
  //   }
  // }, 200);
}, [currentMacroFunzioneId, loadTestData]); // RIMOSSO: isEditingTest, editingTestId, onDataRefreshRequest

  // AGGIUNTO: Callback per operazioni CRUD nella modale Test Modifiche
  const handleTestModificheChanged = useCallback(() => {

  
  // IMPORTANTE: Durante l'editing di un test specifico, NON fare NULLA
  if (isEditingTest || editingTestId) {

    return; // NON fare nulla
  }
  
  // Se non stiamo editando un test specifico, procedi normalmente

  
  if (currentMacroFunzioneId) {

    loadTestData(currentMacroFunzioneId);
    
    // Delayed reloads per essere sicuri
    setTimeout(() => {
      if (!isEditingTest && !editingTestId) { // Doppio controllo

        loadTestData(currentMacroFunzioneId);
      }
    }, 100);
    
    setTimeout(() => {
      if (!isEditingTest && !editingTestId) { // Doppio controllo

        loadTestData(currentMacroFunzioneId);
      }
    }, 500);
  }
  
  // MODIFICATO: Non chiamare onDataRefreshRequest per evitare chiusura modale
  // if (onDataRefreshRequest) {
  //   onDataRefreshRequest();
  // }
}, [currentMacroFunzioneId, loadTestData, isEditingTest, editingTestId]);

  // AGGIUNTO: useEffect per caricare automaticamente i test quando la componente si apre
  useEffect(() => {
    // NON caricare se stiamo editando
    if (!isCreating && currentMacroFunzioneId && !isEditingTest && !editingTestId) {

      loadTestData(currentMacroFunzioneId);
    }
  }, [currentMacroFunzioneId, isCreating, loadTestData, isEditingTest, editingTestId]); // AGGIUNTO: isEditingTest, editingTestId

  // AGGIUNTO: useEffect per ricaricare i test quando dataRefreshKey cambia
  useEffect(() => {
    // NON caricare se stiamo editando
    if (!isCreating && currentMacroFunzioneId && dataRefreshKey > 0 && !isEditingTest && !editingTestId) {

      loadTestData(currentMacroFunzioneId);
    }
  }, [dataRefreshKey, currentMacroFunzioneId, isCreating, loadTestData, isEditingTest, editingTestId]); // AGGIUNTO: isEditingTest, editingTestId

  // Define derived state variables before the return statement
  const hasEssentialFields = !!(formData.MAC_Nome && formData.MAC_Id_Moduli && formData.MAC_Descrizione);
  // const currentMacroFunzioneId = (selectedRow && selectedRow[primaryKey]) || justCreatedId; // Rimosso duplicato


  return (
    <div ref={ref} id={isCreating ? 'detail-view-create' : `detail-view-${selectedRow && selectedRow[primaryKey]}`}
      className={`mt-4 mb-6 p-0 bg-white dark:bg-gray-900 shadow-lg rounded-lg border-2 border-${themeColor}-400 dark:border-${themeColor}-700 overflow-hidden`}>
      
      {/* Header moderno con gradiente */}
      <div className={`px-4 py-3 bg-gradient-to-r from-${themeColor}-100 to-${themeColor}-50 dark:from-${themeColor}-900 dark:to-${themeColor}-800 border-b border-${themeColor}-300 dark:border-${themeColor}-700`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full bg-${themeColor}-200 dark:bg-${themeColor}-700`}>
              {isCreating ? (
                <FaPlusCircle className={`h-5 w-5 text-${themeColor}-700 dark:text-${themeColor}-300`} />
              ) : (
                <FaEdit className={`h-5 w-5 text-${themeColor}-700 dark:text-${themeColor}-300`} />
              )}
            </div>
            <div>
              <h3 className={`text-lg font-bold text-${themeColor}-800 dark:text-${themeColor}-100`}>
                {isCreating 
                  ? 'Nuova Macro Funzione' 
                  : selectedRow?.MAC_Nome 
                    ? `${selectedRow.MAC_Nome} (ID: ${selectedRow[primaryKey]}${selectedRow.MAC_IdRif_MacroFunzioni ? ` · ID Rif: ${selectedRow.MAC_IdRif_MacroFunzioni}` : ''})`
                    : `Macro Funzione ID: ${selectedRow && selectedRow[primaryKey]}${selectedRow?.MAC_IdRif_MacroFunzioni ? ` · ID Rif: ${selectedRow.MAC_IdRif_MacroFunzioni}` : ''}`
                }
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isCreating ? 'Configura i parametri per la nuova macro funzione' : 'Modifica i parametri della macro funzione selezionata'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="p-4 space-y-6">
        {/* SEZIONE 1: Informazioni Generali - Area Params sulla stessa riga di Priorità */}
        <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <FaInfoCircle className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
            <h4 className="text-base font-semibold text-gray-700 dark:text-gray-300">Informazioni Generali</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {fieldGroups.generalFields.map(col => {
              // Priorità e Area Params sulla stessa riga
              if (col.field === 'MAC_Area_Params') {
                return null; // Renderizzato insieme a Priorità
              }
              
              if (col.field === 'MAC_Priorita') {
                // Trova il campo Area Params
                const areaParamsField = fieldGroups.generalFields.find(f => f.field === 'MAC_Area_Params');
                
                return (
                  <React.Fragment key="priorita-area-group">
                    {/* Priorità */}
                    <div>
                      {renderFormFieldSpecial(col)}
                    </div>
                    {/* Area Params - stessa lunghezza di Priorità */}
                    {areaParamsField && (
                      <div>
                        {renderFormFieldSpecial(areaParamsField)}
                      </div>
                    )}
                  </React.Fragment>
                );
              }
              
              return renderFormFieldSpecial(col);
            })}
          </div>
          
          {/* AGGIUNTO: Stato validazione test */}
          <TestValidationStatus 
            testData={testData}
            allTestsPassed={allTestsPassed}
            isLoadingTests={isLoadingTests}
            themeColor={themeColor}
            onRefresh={() => {

              if (currentMacroFunzioneId) {
                loadTestData(currentMacroFunzioneId);
              }
            }}
            // AGGIUNTO: Callback per aprire test in modifica
            onEditTest={handleEditTest}
          />

      
        </div>

        {/* SEZIONE 2: Descrizioni - MIGLIORATA */}
        <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FaEdit className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
              <h4 className="text-base font-semibold text-gray-700 dark:text-gray-300">Descrizione e Note</h4>
            </div>
            {/* NUOVO: Bottone Documentazione Web */}
            {!isCreating && (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setIsDocumentazioneModalOpen(true)}
                  disabled={isProcessing || (!selectedRow && !justCreatedId) || (!selectedRow?.[primaryKey] && !justCreatedId)}
                  
                  className={`px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white
                              bg-blue-600 hover:bg-blue-700 
                              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                              disabled:opacity-50 disabled:cursor-not-allowed
                              flex items-center transition-all duration-200 hover:scale-105`}
                  title="Gestisci documentazione web"
                >
                  <FaBook className="mr-2" />
                  Documentazione
                </button>
                
                <button
                  type="button"
                  onClick={handleDownloadReport}
                  disabled={isProcessing || isGeneratingReport || (!selectedRow && !justCreatedId) || (!selectedRow?.[primaryKey] && !justCreatedId)}
                  
                  className={`px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white
                              bg-red-600 hover:bg-red-700 
                              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 
                              disabled:opacity-50 disabled:cursor-not-allowed
                              flex items-center transition-all duration-200 hover:scale-105`}
                  title="Scarica report storico PDF"
                >
                  {isGeneratingReport ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Generazione...
                    </>
                  ) : (
                    <>
                      <FaFilePdf className="mr-2" />
                      Report Macrofunzione
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {fieldGroups.descriptiveFields.map(col => (
              <div key={col.field} className={['MAC_Descrizione', 'MAC_Modifiche'].includes(col.field) ? 'lg:col-span-2' : ''}>
                {renderFormFieldSpecial(col)}
              </div>
            ))}
          </div>
        </div>
        
        {/* SEZIONE 3: Configurazioni - MODIFICATA per includere Test Modifiche */}
        {!isCreating && (
          <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <div className="flex items-center space-x-2 mb-4">
              <FaCog className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />
              <h4 className="text-base font-semibold text-gray-700 dark:text-gray-300">Configurazioni</h4>
            </div>
            
            {/* MODIFICATO: Cambia da grid-cols-2 a grid-cols-3 per 3 pulsanti */}
            <div className="grid grid-cols-3 gap-4">
              {/* Tabella Associata button - Esistente */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                  Tabella Associata
                </label>
                <button
                  type="button"
                  onClick={() => {

                    handleOpenCampiTabellaModal();
                  }}
                  disabled={isProcessing || (!formData.MAC_Nome || !formData.MAC_Id_Moduli || !formData.MAC_Descrizione)}
                  
                  className={`w-full px-3 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white 
                             ${(!formData.MAC_Id_Tabelle || formData.MAC_Id_Tabelle === 0 || formData.MAC_Id_Tabelle === '0')
                               ? `bg-orange-600 hover:bg-orange-700 focus:ring-orange-500` 
                               : `bg-${themeColor}-600 hover:bg-${themeColor}-700 focus:ring-${themeColor}-500`
                             } 
                             focus:outline-none focus:ring-2 focus:ring-offset-2 
                             disabled:opacity-50 disabled:cursor-not-allowed 
                             flex items-center justify-center transition-all duration-200 hover:scale-105`}
                >
                  {(isCreatingTable || isLoadingCampi) ? (
                    <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                  ) : (!formData.MAC_Id_Tabelle || formData.MAC_Id_Tabelle === 0 || formData.MAC_Id_Tabelle === '0') ? (
                    <FaPlusCircle className="mr-2 h-4 w-4" />
                  ) : (
                    <FaEdit className="mr-2 h-4 w-4" />
                  )}
                  
                  {(!formData.MAC_Id_Tabelle || formData.MAC_Id_Tabelle === 0 || formData.MAC_Id_Tabelle === '0')
                    ? "Crea Tabella" 
                    : `Modifica Campi (${formData.MAC_Nome_Tabelle || formData.MAC_Id_Tabelle})`
                  }
                </button>
              </div>

              {/* Funzioni Aggiuntive - Esistente */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                  Gestione Avanzata
                </label>
                <button
                  type="button"
                  onClick={() => setIsFunzioniAggiuntiveModalOpen(true)}
                  disabled={isProcessing || (!selectedRow && !justCreatedId) || (!selectedRow?.[primaryKey] && !justCreatedId)}
                  
                  className={`w-full px-3 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white
                              bg-purple-600 hover:bg-purple-700 
                              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 
                              disabled:opacity-50 disabled:cursor-not-allowed
                              flex items-center justify-center transition-all duration-200 hover:scale-105`}
                  title="Gestisci funzioni aggiuntive"
                >
                  <FaPlusCircle className="mr-2 h-4 w-4" /> 
                  Funzioni Aggiuntive
                </button>
              </div>

              {/* NUOVO: Test Modifiche */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                  Test & QA
                </label>
                <button
                  type="button"
                  onClick={() => setIsTestModificheModalOpen(true)}
                  disabled={isProcessing || (!selectedRow && !justCreatedId) || (!selectedRow?.[primaryKey] && !justCreatedId)}
                  
                  className={`w-full px-3 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white
                              bg-indigo-600 hover:bg-indigo-700 
                              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 
                              disabled:opacity-50 disabled:cursor-not-allowed
                              flex items-center justify-center transition-all duration-200 hover:scale-105`}
                  title="Gestisci test e modifiche"
                >
                  Test Modifiche
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SEZIONE 4: CRUD Operations - Migliorata */}
        <CrudOperationsGrid
          selectedRow={formData}
          theme={theme}
          onInputChange={handleInputChange}
          isProcessing={isProcessing}
          inputTypes={inputTypes}
          compactMode={true}
        />

        {/* SEZIONE 5: Opzioni Sistema - Semplificata */}
        <SystemOptions
          fieldGroups={fieldGroups}
          formData={formData}
          handleInputChange={handleInputChange}
          isProcessing={isProcessing}
          themeColor={themeColor}
        />

        {/* Pulsanti azione moderni */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing || isCensimentoFunzioni}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 flex items-center">
              <FaTimes className="mr-2 h-4 w-4" />
              Annulla
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* NUOVO: Checkbox per censimento automatico - solo in edit mode */}
            {!isCreating && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="censisci-funzioni-auto"
                  checked={censisciFunzioniFlag}
                  onChange={(e) => setCensisciFunzioniFlag(e.target.checked)}
                  disabled={isProcessing || isCensimentoFunzioni}
                  className={`form-checkbox h-4 w-4 text-${themeColor}-600 rounded focus:ring-${themeColor}-500`}
                />
                <label 
                  htmlFor="censisci-funzioni-auto" 
                  className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center cursor-pointer"
                >
                  <FaCogs className="mr-1 h-3 w-3" />
                  Censisci Funzioni
                </label>
              </div>
            )}
            
            <button
              type="submit"
              disabled={
                isProcessing || 
                isCensimentoFunzioni ||
                !hasEssentialFields ||
                isEditingTest ||  // AGGIUNTO: Disabilita durante editing test
                editingTestId     // AGGIUNTO: Disabilita quando c'è un test in editing
              }
              className={`px-4 py-2 text-sm bg-${themeColor}-600 text-white rounded-lg hover:bg-${themeColor}-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm hover:shadow-md`}
              title={
                !hasEssentialFields ? "Compila Nome, Modulo e Descrizione per procedere" : 
                (isEditingTest || editingTestId) ? "Completa l'editing del test prima di salvare" :
                ""
              }
            >
              {(isProcessing || isCensimentoFunzioni) ? (
                <FaSpinner className="animate-spin mr-2 h-4 w-4" />
              ) : (
                isCreating ? <FaPlusCircle className="mr-2 h-4 w-4" /> : <FaSave className="mr-2 h-4 w-4" />
              )}
              {(isProcessing || isCensimentoFunzioni) ? 
                (isCensimentoFunzioni ? 'Censimento...' : 'Elaborazione...') :
                (isCreating ? 'Crea e Modifica' : (censisciFunzioniFlag ? 'Salva e Censisci' : 'Salva'))
              }
            </button>
          </div>
        </div>
      </form>

      {/* Modals */}
      {/* Routing condizionale: per il modulo HUB/MILO si usa la modale dedicata
          `CampiTableModalHub`, in tutti gli altri casi la modale standard INVARIATA. */}
      {isCampiTabellaModalOpen && isCurrentHubModule && (
        <CampiTableModalHub
          key={`campi-modal-hub-${currentTableIdForModal}-${campiModalRefreshKey}`}
          isOpen={isCampiTabellaModalOpen}
          onClose={() => setIsCampiTabellaModalOpen(false)}
          currentTableId={currentTableIdForModal}
          initialCampi={campiTabellaAssociata}
          onCampiUpdated={() => {
            if (currentTableIdForModal || formData.MAC_Id_Tabelle) {
              setTimeout(() => {
                fetchCampiPerTabella(currentTableIdForModal || formData.MAC_Id_Tabelle);
              }, 100);
            }
          }}
        />
      )}
      {isCampiTabellaModalOpen && !isCurrentHubModule && (
        <CampiTabellaModal
          key={`campi-modal-${currentTableIdForModal}-${campiModalRefreshKey}`}
          isOpen={isCampiTabellaModalOpen}
          onClose={() => setIsCampiTabellaModalOpen(false)}
          currentTableId={currentTableIdForModal}
          initialCampi={campiTabellaAssociata}
          isLoadingCampi={isLoadingCampi}
          onCampiUpdated={() => {
            if (currentTableIdForModal || formData.MAC_Id_Tabelle) {
              setTimeout(() => {
                fetchCampiPerTabella(currentTableIdForModal || formData.MAC_Id_Tabelle);
              }, 100);
            }
          }}
          themeColor={themeColor}
          macrofunzioneData={selectedRow || formData} // AGGIUNTO: Passa i dati della macrofunzione
        />
      )}

      {isFunzioniAggiuntiveModalOpen && (
        <FunzioniAggiuntiveModal
          isOpen={isFunzioniAggiuntiveModalOpen}
          onClose={handleFunzioniAggiuntiveClose} // MODIFICATO: Usa il callback separato
          macroFunzioneId={currentMacroFunzioneIdRif}
          themeColor={themeColor}
          onDataChanged={handleFunzioniAggiuntiveChanged}
            macroFunzioneData={selectedRow || formData} 
        />
      )}

      {/* AGGIORNATO: TestModificheModal con logica di mode dinamica */}
      {isTestModificheModalOpen && (
        <TestModificheModal
          isOpen={isTestModificheModalOpen}
          onClose={handleTestModificheClose}
          onDataChanged={handleTestModificheChanged}
          macroFunzioneId={currentMacroFunzioneIdRif}
          macroFunzioneData={selectedRow || formData}
          editTestId={editingTestId}
          // ✅ CORREZIONE: Mode dinamica basata su editingTestId
          mode={editingTestId ? "edit" : "list"}
          themeColor={themeColor}
        />
      )}

      {/* NUOVO: DocumentazioneWebModal */}
      {isDocumentazioneModalOpen && (
        <DocumentazioneWebModal
          isOpen={isDocumentazioneModalOpen}
          onClose={() => setIsDocumentazioneModalOpen(false)}
          macroFunzioneId={currentMacroFunzioneIdRif}
          macroFunzioneNome={formData.MAC_Nome}
          onDataChanged={() => {
            // Opzionale: refresh se necessario
          }}
        />
      )}

      {/* NUOVO: Modale per selezione acronimo tabella */}
      {isTableAcronymModalOpen && (
        <TableAcronymModal
          isOpen={isTableAcronymModalOpen}
          onClose={handleAcronymCancel}
          onConfirm={handleAcronymSelected}
          tableName={formData.MAC_Nome}
          themeColor={themeColor}
        />
      )}

      {/* NUOVO: Modale di conferma per creazione tabella */}
      {isCreateTableConfirmOpen && pendingTableCreation && (
        <CreateTableConfirmModal
          isOpen={isCreateTableConfirmOpen}
          onClose={handleCreateTableCancel}
          onConfirm={handleCreateTableConfirm}
          macroFunzioneData={formData}
          moduleData={{
            ...pendingTableCreation.moduleData,
            acronym: selectedAcronym // Aggiungi l'acronimo ai dati
          }}
          themeColor={themeColor}
        />
      )}

      {/* Aggiungi anche messaggi informativi dopo il form per guidare l'utente */}
      {errors.general && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">
            <FaTimesCircle className="inline mr-1" />
            {errors.general}
          </p>
        </div>
      )}

      {isCreating && !hasEssentialFields && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-600 dark:text-amber-400">
            <FaInfoCircle className="inline mr-1" />
            Per creare una macro funzione sono richiesti: <strong>Nome</strong>, <strong>Modulo</strong> e <strong>Descrizione</strong>
          </p>
        </div>
      )}

      {/* NEW: Add informational message about configurations being available after creation */}
      {isCreating && hasEssentialFields && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-600 dark:text-blue-400">
            <FaInfoCircle className="inline mr-1" />
            Le configurazioni avanzate (Tabella Associata e Funzioni Aggiuntive) saranno disponibili dopo la creazione del record.
          </p>
        </div>
      )}

     
    </div>
  );
});

export default MacroFunzioniDetailView;
